/** Sponsored ads surface shown in the unified campaigns table (not URL segments). */
export type CampaignAdProduct = 'SP' | 'SB' | 'SD';

export function campaignAdProductLabel(p: CampaignAdProduct): string {
  switch (p) {
    case 'SB':
      return 'Sponsored Brands';
    case 'SD':
      return 'Sponsored Display';
    default:
      return 'Sponsored Products';
  }
}

export type BudgetRuleApiProduct = 'sp' | 'sb' | 'sd';

export function campaignAdProductToBudgetRuleApiProduct(p: CampaignAdProduct): BudgetRuleApiProduct {
  return p.toLowerCase() as BudgetRuleApiProduct;
}

function parseCampaignAdProduct(raw: Record<string, unknown>): CampaignAdProduct {
  const v = raw.__adProduct ?? raw.adProduct;
  if (v === 'SB' || v === 'SPONSORED_BRANDS') return 'SB';
  if (v === 'SD' || v === 'SPONSORED_DISPLAY') return 'SD';
  return 'SP';
}

function normalizeDisplayDate(raw: unknown): string | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const s = String(raw).trim();
  if (/^\d{8}$/.test(s)) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }
  return s;
}

/** Normalized campaign row for UI (Amazon payloads vary by ad product). */
export interface SpCampaignNormalized {
  campaignId: string;
  name: string;
  state: string;
  budget: number;
  budgetType: string;
  /** Sponsored Products / Brands / Display — from merged fetch, not Amazon field names in URLs. */
  adProduct: CampaignAdProduct;
  startDate?: string;
  endDate?: string;
  raw: Record<string, unknown>;
}

export function normalizeSpCampaign(raw: Record<string, unknown>): SpCampaignNormalized {
  const campaignId = String(raw.campaignId ?? raw.campaign_id ?? '');
  const name = String(raw.name ?? 'Unnamed');
  const state = String(raw.state ?? 'UNKNOWN').toUpperCase();
  const adProduct = parseCampaignAdProduct(raw);
  let budget = 0;
  let budgetType = 'DAILY';
  if (typeof raw.budget === 'number') {
    budget = raw.budget;
  } else if (raw.budget && typeof raw.budget === 'object') {
    const b = raw.budget as Record<string, unknown>;
    if (typeof b.budget === 'number') budget = b.budget;
    if (typeof b.budgetType === 'string') budgetType = b.budgetType.toUpperCase();
  }
  if (typeof raw.budgetType === 'string') budgetType = raw.budgetType.toUpperCase();
  return {
    campaignId,
    name,
    state,
    budget,
    budgetType,
    adProduct,
    startDate: normalizeDisplayDate(raw.startDate),
    endDate: normalizeDisplayDate(raw.endDate),
    raw,
  };
}
